import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSettings } from '../../../context/SettingsContext';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../../components/forms/formValidationUtils';

interface ConfigFormValues {
    company_name: string;
    currency: string;
    vat_percentage: string;
    labour_rate_per_hour: string;
    overhead_rate_per_sqm: string;
    density_grc: string;
    density_grp: string;
}

const SystemConfig: React.FC = () => {
    const { settings, updateSetting, refreshSettings } = useSettings();
    const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm<ConfigFormValues>();
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        if (settings.length > 0) {
            const companyName = settings.find(s => s.key === 'company_name')?.value || '';
            const currency = settings.find(s => s.key === 'currency')?.value || '';
            const vatPercentage = settings.find(s => s.key === 'vat_percentage' || s.key === 'tax_rate')?.value || '';
            const labourRate = settings.find(s => s.key === 'labour_rate_per_hour')?.value || '';
            const overheadRate = settings.find(s => s.key === 'overhead_rate_per_sqm')?.value || '';
            const densityGrc = settings.find(s => s.key === 'density_grc')?.value || '';
            const densityGrp = settings.find(s => s.key === 'density_grp')?.value || '';

            setValue('company_name', companyName);
            setValue('currency', currency);
            setValue('vat_percentage', vatPercentage);
            setValue('labour_rate_per_hour', labourRate);
            setValue('overhead_rate_per_sqm', overheadRate);
            setValue('density_grc', densityGrc);
            setValue('density_grp', densityGrp);
        }
    }, [settings, setValue]);

    const onSubmit = async (data: ConfigFormValues) => {
        try {
            await Promise.all([
                updateSetting('company_name', data.company_name),
                updateSetting('currency', data.currency),
                updateSetting('vat_percentage', data.vat_percentage),
                updateSetting('labour_rate_per_hour', data.labour_rate_per_hour),
                updateSetting('overhead_rate_per_sqm', data.overhead_rate_per_sqm),
                updateSetting('density_grc', data.density_grc),
                updateSetting('density_grp', data.density_grp),
            ]);
            toast.success('Settings updated successfully');
            refreshSettings();
        } catch (error) {
            toast.error('Failed to update settings');
            console.error(error);
        }
    };
    const onInvalid = (formErrors: Record<string, any>) => {
        showValidationSummaryToast();
        focusFirstInvalidField(Object.keys(formErrors)[0]);
    };

    const tabs = [
        { id: 'general', label: 'General' },
        { id: 'pricing', label: 'Pricing Rules' },
    ];

    return (
        <div className="container mx-auto py-10">
            <h1 className="text-3xl font-bold mb-6">System Configuration</h1>

            <div className="mb-6">
                <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle>{activeTab === 'general' ? 'General Settings' : 'Pricing Rules'}</CardTitle>
                    <CardDescription>
                        {activeTab === 'general'
                            ? 'Manage your company details and system defaults.'
                            : 'Configure base rates for estimation calculations.'}
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                    <CardContent className="space-y-4">
                        {activeTab === 'general' && (
                            <>
                                <div className="space-y-2">
                                    <RequiredLabel htmlFor="company_name" required>Company Name</RequiredLabel>
                                    <Input
                                        id="company_name"
                                        placeholder="Enter company name"
                                        aria-invalid={!!errors.company_name}
                                        aria-required="true"
                                        aria-describedby={errors.company_name ? 'company_name_error' : undefined}
                                        {...register('company_name', { required: true })}
                                    />
                                    <FieldErrorText id="company_name_error" message={errors.company_name ? 'Company Name is required' : undefined} />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="currency">Currency</Label>
                                        <Input
                                            id="currency"
                                            placeholder="AED"
                                            {...register('currency')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="vat_percentage">Default VAT Rate (%)</Label>
                                        <Input
                                            id="vat_percentage"
                                            placeholder="5"
                                            type="number"
                                            step="0.01"
                                            {...register('vat_percentage')}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'pricing' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">Labour & Overhead</h3>
                                    <div className="space-y-2">
                                        <Label htmlFor="labour_rate_per_hour">Labour Rate (AED/Hour)</Label>
                                        <Input
                                            id="labour_rate_per_hour"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            {...register('labour_rate_per_hour')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="overhead_rate_per_sqm">Overhead Rate (AED/SQM)</Label>
                                        <Input
                                            id="overhead_rate_per_sqm"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            {...register('overhead_rate_per_sqm')}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">Material Densities</h3>
                                    <div className="space-y-2">
                                        <Label htmlFor="density_grc">GRC Density (kg/m³)</Label>
                                        <Input
                                            id="density_grc"
                                            type="number"
                                            step="0.01"
                                            placeholder="2100"
                                            {...register('density_grc')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="density_grp">GRP Density (kg/m³)</Label>
                                        <Input
                                            id="density_grp"
                                            type="number"
                                            step="0.01"
                                            placeholder="1600"
                                            {...register('density_grp')}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                    <div className="flex justify-between p-6 pt-0">
                        <Button variant="outline" type="button" onClick={() => refreshSettings()}>
                            Reset
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default SystemConfig;
